import { Outlet, Link, useNavigate } from "react-router-dom";
import { Button, Navbar as HeroNavbar, NavbarBrand, NavbarContent, NavbarItem, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, DropdownSection, Avatar, Skeleton, Chip, Tooltip } from "@heroui/react";
import { User, Shield, HelpCircle, LogOut, BotMessageSquare, CheckCircle2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ScrollToTop } from "../components/ScrollToTop";
import { ThemeSwitch } from "../components/ThemeSwitch";
import { CreditBadge } from "../components/CreditBadge";
import { useWhatsAppLink } from "../contexts/WhatsAppLinkContext";
import useScreenSize, { ScreenSize } from "../hooks/useScreenSize";

/**
 * WhatsApp Icon component
 */
const WhatsAppIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
);

export function PublicLayout() {
    const { isAuthenticated, user, logout, loading } = useAuth();
    const { needsLinking, isLinked, openLinkModal, openWhatsApp } = useWhatsAppLink();
    const navigate = useNavigate();
    const screenSize = useScreenSize();
    const isMobile = screenSize === ScreenSize.Mobile;

    const handleLogout = () => {
        logout();
        navigate('/auth');
    };

    return (
        <div className="flex flex-col h-screen bg-card hover:shadow-custom transition-shadow overflow-hidden">
            <ScrollToTop />

            {/* Public Navbar */}
            <HeroNavbar
                isBordered
                maxWidth="full"
                height="3.5rem"
                position="static"
                className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 flex-shrink-0"
            >
                <NavbarBrand>
                    <Link to="/" className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                            <BotMessageSquare className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <p className="font-bold text-lg text-foreground">PascoHub</p>
                    </Link>
                </NavbarBrand>

                <NavbarContent justify="end">
                    {/* WhatsApp Link Button - Only show when user needs to link */}
                    {isAuthenticated && user && needsLinking && (
                        <NavbarItem className="hidden sm:flex">
                            <Tooltip
                                content="Link WhatsApp to chat with PASCOs on the go!"
                                placement="bottom"
                                classNames={{ content: "rounded text-xs max-w-[200px] text-center" }}
                            >
                                <Button
                                    size="sm"
                                    variant="flat"
                                    className="rounded bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] border-none gap-1.5"
                                    onPress={openLinkModal}
                                    startContent={<WhatsAppIcon className="h-4 w-4" />}
                                >
                                    <span className="hidden md:inline">Link WhatsApp</span>
                                </Button>
                            </Tooltip>
                        </NavbarItem>
                    )}

                    {/* Theme Switcher */}
                    <NavbarItem>
                        <ThemeSwitch isIconOnly />
                    </NavbarItem>

                    {/* Credit Badge - Show for authenticated users */}
                    {isAuthenticated && user && (
                        <NavbarItem>
                            <CreditBadge showLabel={false} size="sm" />
                        </NavbarItem>
                    )}

                    {loading ? (
                        <NavbarItem>
                            <Skeleton className="h-8 w-8 rounded-full" />
                        </NavbarItem>
                    ) : isAuthenticated && user ? (
                        <>
                            {/* User Profile Dropdown */}
                            <NavbarItem>
                                <Dropdown showArrow placement="bottom-end"
                                    classNames={{
                                        content: 'rounded'
                                    }}
                                >
                                    <DropdownTrigger>
                                        <Avatar
                                            isBordered
                                            as="button"
                                            className="transition-transform cursor-pointer hover:opacity-80"
                                            color="primary"
                                            src={user?.profilePictureUrl || undefined}
                                            name={user?.displayName || `${user.firstName} ${user.lastName}`}
                                            size="sm"
                                            showFallback
                                            fallback={<User className="h-4 w-4" />}
                                        />
                                    </DropdownTrigger>
                                    <DropdownMenu
                                        aria-label="User menu actions"
                                        classNames={{
                                            base: "text-primary min-w-[220px] rounded",

                                        }}
                                    >
                                        <DropdownSection
                                            title="Signed in as"
                                            classNames={{
                                                heading: "text-xs font-semibold text-default-400 uppercase",
                                            }}
                                        >
                                            <DropdownItem
                                                key="user-info"
                                                className="h-14 gap-2"
                                                textValue="User info"
                                                isReadOnly
                                                classNames={{
                                                    base: "rounded",
                                                }}
                                            >
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-semibold">
                                                        {user?.displayName || `${user.firstName} ${user.lastName}`}
                                                    </span>
                                                    <span className="text-xs text-default-500">{user.email}</span>
                                                </div>
                                            </DropdownItem>
                                        </DropdownSection>

                                        <DropdownSection
                                            title="Account"
                                            classNames={{
                                                heading: "text-xs font-semibold text-default-400 uppercase",
                                            }}
                                        >
                                            <DropdownItem
                                                key="profile"
                                                className="hover:text-foreground rounded"
                                                onPress={() => navigate('/profile')}
                                                startContent={<User size={16} />}
                                            >
                                                My Profile
                                            </DropdownItem>

                                            {/* <DropdownItem
                                                key="account-settings"
                                                className="hover:text-foreground rounded"
                                                onPress={() => navigate('/account/settings')}
                                                startContent={<Settings size={16} />}
                                            >
                                                Account Settings
                                            </DropdownItem> */}

                                            <DropdownItem
                                                key="security"
                                                className="hover:text-foreground rounded"
                                                onPress={() => navigate('/account/security')}
                                                startContent={<Shield size={16} />}
                                            >
                                                Security
                                            </DropdownItem>

                                            {/* WhatsApp Link - Only show on mobile where navbar button is hidden */}
                                            <DropdownItem
                                                key="whatsapp"
                                                className={isMobile
                                                    ? (needsLinking
                                                        ? "rounded bg-[#25D366]/5 text-[#25D366] hover:bg-[#25D366]/10"
                                                        : "hover:text-foreground rounded")
                                                    : "hidden"}
                                                onPress={needsLinking ? openLinkModal : openWhatsApp}
                                                startContent={<WhatsAppIcon className="h-4 w-4" />}
                                                endContent={
                                                    needsLinking ? (
                                                        <Chip size="sm" color="success" variant="flat" className="h-5 text-[10px]">
                                                            New
                                                        </Chip>
                                                    ) : isLinked ? (
                                                        <CheckCircle2 size={14} className="text-[#25D366]" />
                                                    ) : null
                                                }
                                            >
                                                {needsLinking ? "Link WhatsApp" : "WhatsApp"}
                                            </DropdownItem>
                                        </DropdownSection>

                                        <DropdownSection
                                            title="Support"
                                            classNames={{
                                                heading: "text-xs font-semibold text-default-400 uppercase",
                                            }}
                                        >
                                            <DropdownItem
                                                key="help"
                                                className="hover:text-foreground rounded"
                                                onPress={() => navigate('/help')}
                                                startContent={<HelpCircle size={16} />}
                                            >
                                                Help Center
                                            </DropdownItem>
                                        </DropdownSection>

                                        <DropdownSection>
                                            <DropdownItem
                                                key="logout"
                                                className="text-danger hover:bg-danger/10 rounded"
                                                color="danger"
                                                onPress={handleLogout}
                                                startContent={<LogOut size={16} />}
                                            >
                                                Logout
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            </NavbarItem>
                        </>
                    ) : (
                        <>
                            <NavbarItem>
                                <Button
                                    as={Link}
                                    to="/auth"
                                    variant="solid"
                                    color="primary"
                                    size="sm"
                                    className="rounded"
                                >
                                    Login
                                </Button>
                            </NavbarItem>
                        </>
                    )}
                </NavbarContent>
            </HeroNavbar>

            {/* Main Content */}
            <main className="flex-1 overflow-hidden">
                <Outlet />
            </main>
        </div>
    );
}
